
import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenAI, Chat } from "@google/genai";
import { ChatIcon } from './IconComponents';

interface Message {
  sender: 'user' | 'bot';
  text: string;
}

const Chatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      sender: 'bot',
      text: "¡Hola! Soy tu asistente virtual. ¿Cómo puedo ayudarte con tu caso de peritajes, multas o defensa legal en Europa?",
    },
  ]);
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [chat, setChat] = useState<Chat | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      if (!process.env.API_KEY) {
        console.error("API_KEY no está configurada.");
        setMessages(prev => [...prev, { sender: 'bot', text: 'Lo siento, el asistente virtual no está disponible en este momento por un error de configuración.' }]);
        return;
      }
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const newChat = ai.chats.create({
        model: 'gemini-3-flash-preview',
        config: {
          systemInstruction: `Eres un asistente virtual para Peritajes.EU, una empresa que ofrece informes periciales, defensa de multas y representación legal en toda Europa. Tu grupo incluye Multas.EU y Juzgados.EU.
          Tu objetivo es:
          1.  Entender la consulta del usuario de forma amable y profesional.
          2.  Proporcionar información general y útil sobre los servicios que ofrecen. No des consejos legales específicos, ya que no eres un abogado.
          3.  Si el problema del usuario es complejo o requiere atención de un experto, anímale a rellenar el formulario de contacto para que un especialista analice su caso. Menciona que la consulta inicial es sin compromiso.
          4.  Sé conciso y claro en tus respuestas. Usa un tono cercano pero profesional.
          5.  Si no entiendes algo, pide al usuario que lo aclare.
          6.  Nunca digas que eres un modelo de lenguaje o una IA. Preséntate siempre como el asistente virtual de Peritajes.EU.`,
        },
      });
      setChat(newChat);
    } catch (error) {
      console.error("Error initializing Gemini Chat:", error);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Lo siento, el asistente virtual no está disponible en este momento.' }]);
    }
  }, []);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages, isLoading]);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const handleSendMessage = async () => {
    if (userInput.trim() === '' || isLoading || !chat) return;

    const userMessage: Message = { sender: 'user', text: userInput };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = userInput;
    setUserInput('');
    setIsLoading(true);

    try {
      const result = await chat.sendMessageStream({ message: currentInput });
      let botResponse = '';
      setMessages(prev => [...prev, { sender: 'bot', text: '' }]);
      
      for await (const chunk of result) {
        botResponse += chunk.text;
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1].text = botResponse;
          return newMessages;
        });
      }

    } catch (error) {
      console.error('Error sending message to Gemini:', error);
      setMessages(prev => [...prev, { sender: 'bot', text: 'Lo siento, ha ocurrido un error. Por favor, intenta de nuevo o utiliza el formulario de contacto.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={`fixed bottom-24 right-5 z-50 transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`} style={{width: '350px', height: '500px'}}>
        <div className="w-full h-full bg-white rounded-xl shadow-2xl flex flex-col">
          <header className="bg-blue-600 text-white p-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
            <h3 className="font-bold text-lg">Asistente Virtual</h3>
            <button onClick={toggleChat} className="text-2xl leading-none" aria-label="Cerrar chat">&times;</button>
          </header>
          <div className="flex-1 p-4 overflow-y-auto bg-gray-50">
            <div className="space-y-4">
              {messages.map((msg, index) => (
                <div key={index} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-2xl shadow-sm ${msg.sender === 'user' ? 'bg-blue-500 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none'}`}>
                    {msg.text.split('\n').map((line, i) => <p key={i} className="whitespace-pre-wrap">{line || '\u00A0'}</p>)}
                  </div>
                </div>
              ))}
              {isLoading && (
                 <div className="flex justify-start">
                    <div className="max-w-[85%] p-3 rounded-2xl shadow-sm bg-white text-gray-800 rounded-bl-none flex items-center space-x-1.5">
                        <span className="w-2 h-2 bg-gray-500 rounded-full inline-block animate-bounce"></span>
                        <span className="w-2 h-2 bg-gray-500 rounded-full inline-block animate-bounce" style={{animationDelay: '0.2s'}}></span>
                        <span className="w-2 h-2 bg-gray-500 rounded-full inline-block animate-bounce" style={{animationDelay: '0.4s'}}></span>
                    </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <footer className="p-3 border-t border-gray-200 bg-white rounded-b-xl flex-shrink-0">
            <div className="flex items-center">
              <input
                type="text"
                value={userInput}
                onChange={e => setUserInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey ? (e.preventDefault(), handleSendMessage()) : null}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={isLoading || !userInput.trim()}
                className="ml-3 bg-blue-600 text-white p-2.5 rounded-full hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                aria-label="Enviar mensaje"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 transform rotate-90">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </footer>
        </div>
      </div>
      
      <button 
        onClick={toggleChat} 
        className="fixed bottom-5 right-5 z-50 bg-blue-600 text-white w-16 h-16 rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all transform hover:scale-110"
        aria-label="Abrir chat de asistente virtual"
      >
        <ChatIcon className="w-8 h-8" />
      </button>
    </>
  );
};

export default Chatbot;
